/**
 * API-клиент уведомлений colver (`/api/user/colver/v1/notifications`).
 *
 * Auth — стандартный boilerplate: `X-Tests-User-Sid` в dev.
 */
import axios from 'axios';
import {readStoredSid} from '@api/auth';
import type {
  BackendNotification,
  ChannelInfo,
  ChannelInitResponse,
  ChannelName,
  NotificationSettings,
  NotificationUpdateRequest,
  NotificationsListRequest,
  UnreadCountResponse,
  ReadAllResponse,
  UpdateSettingsDTO,
} from './types';

const http = axios.create({
  baseURL: import.meta.env.VITE_MEEREC_BASE ?? '/api/user/meerec/v1',
});

const fallbackDevSid = import.meta.env.VITE_DEV_USER_SID;
http.interceptors.request.use((config) => {
  const sid = readStoredSid() ?? fallbackDevSid;
  if (sid) config.headers.set('X-Tests-User-Sid', sid);
  return config;
});

export const notificationsClient = {
  // ─── Лента ────────────────────────────────────────────────────────────────

  /** Лента уведомлений (свежие сверху, occurredAt desc). */
  async list(
    workspaceSid: string,
    params: NotificationsListRequest = {},
  ): Promise<BackendNotification[]> {
    const {limit, ...queryParams} = params;
    const query = new URLSearchParams({
      workspaceId: workspaceSid,
      ...(queryParams.before && {before: queryParams.before}),
      ...(queryParams.kind && {kind: queryParams.kind}),
      ...(queryParams.unreadOnly !== undefined && {
        unreadOnly: String(queryParams.unreadOnly),
      }),
    });
    const {data} = await http.post<BackendNotification[]>(
      `/notifications?${query}`,
      {limit}, // тело: только limit/sort/filter/projection
    );
    return data;
  },

  /** Счётчик непрочитанных (бейдж). GET по контракту. */
  async count(workspaceSid: string): Promise<UnreadCountResponse> {
    const {data} = await http.get<UnreadCountResponse>(`/notifications/count`, {
      params: {workspaceId: workspaceSid},
    });
    return data;
  },

  /** Карточка после повторной проверки текущего source scope сервером. */
  async get(notificationSid: string): Promise<BackendNotification> {
    const {data} = await http.get<BackendNotification>(
      `/notifications/${notificationSid}`,
    );
    return data;
  },

  /** Скрыть карточку, сохранив серверную историю доставки. */
  async remove(notificationSid: string): Promise<void> {
    await http.delete(`/notifications/${notificationSid}`);
  },

  /** Отметить собственную карточку прочитанной. */
  async update(
    notificationSid: string,
    body: NotificationUpdateRequest,
  ): Promise<BackendNotification> {
    const {data} = await http.patch<BackendNotification>(
      `/notifications/${notificationSid}`,
      body,
    );
    return data;
  },

  /** Обработать одну порцию read-all; continuation возвращает сервер. */
  async readAll(
    workspaceSid: string,
    before?: string,
  ): Promise<ReadAllResponse> {
    const {data} = await http.post<ReadAllResponse>(
      '/notifications/read-all',
      undefined,
      {params: {workspaceId: workspaceSid, ...(before && {before})}},
    );
    return data;
  },

  // ─── Настройки ────────────────────────────────────────────────────────────

  /** Получить настройки (сервер создаёт дефолт при первом GET). */
  async getSettings(): Promise<NotificationSettings> {
    const {data} = await http.get<NotificationSettings>(
      `/notifications/settings`,
    );
    return data;
  },

  /** Обновить только переданные известные секции настроек. */
  async updateSettings(body: UpdateSettingsDTO): Promise<NotificationSettings> {
    const {data} = await http.put<NotificationSettings>(
      `/notifications/settings`,
      body,
    );
    return data;
  },

  // ─── Каналы доставки ───────────────────────────────────────────────────────

  /** Список подключённых каналов. */
  async getChannels(): Promise<ChannelInfo[]> {
    const {data} = await http.get<ChannelInfo[]>(
      `/notifications/settings/channels`,
    );
    return data;
  },

  /**
   * Подключить канал-бот — возвращает код верификации + ссылку на бота.
   * Эндпоинт общий: `/channels/{channel}/init` (telegram, max).
   */
  async initChannel(channel: ChannelName): Promise<ChannelInitResponse> {
    const {data} = await http.post<ChannelInitResponse>(
      `/notifications/settings/channels/${channel}/init`,
    );
    return data;
  },

  /** Отключить канал. */
  async disconnectChannel(connectionSid: string): Promise<void> {
    await http.delete(`/notifications/settings/channels/${connectionSid}`);
  },
};
